// Admin Country Pricing — /admin/country-pricing, permission: manage_settings
// CRUD over the `country_pricing` collection: per-country base price, strikethrough
// price, tax rate/label, and which payment gateways are offered for that country.
// India (IN) is the seeded default and can't be deleted (see routes/country_pricing.py).
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Globe } from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const COUNTRY_FLAGS = {
  IN: '🇮🇳', AU: '🇦🇺', US: '🇺🇸', GB: '🇬🇧', SG: '🇸🇬', AE: '🇦🇪',
  CA: '🇨🇦', NZ: '🇳🇿', MY: '🇲🇾', DE: '🇩🇪', FR: '🇫🇷', JP: '🇯🇵',
};

const GATEWAY_BADGES = {
  razorpay: { label: 'Razorpay', color: '#3395FF' },
  stripe: { label: 'Stripe', color: '#6772E5' },
  paypal: { label: 'PayPal', color: '#003087' },
};

const ALL_GATEWAYS = ['razorpay', 'stripe', 'paypal'];

const EMPTY_FORM = {
  country_code: '', country_name: '', currency_code: '',
  currency_symbol: '', currency_name: '',
  base_price: '', original_price: '',
  tax_rate: 0, tax_label: 'GST', tax_inclusive: false,
  badge_text: 'BEST VALUE', enabled: true,
  payment_gateways: ['stripe', 'paypal'],
  reaccess_price: '', reaccess_package_name: 'Study Again Pass',
};

const FORM_FIELDS = [
  { label: 'Country Code *', key: 'country_code', placeholder: 'AU', hint: 'ISO 3166-1 alpha-2 (e.g. AU, GB, US)', lockOnEdit: true },
  { label: 'Country Name *', key: 'country_name', placeholder: 'Australia' },
  { label: 'Currency Code *', key: 'currency_code', placeholder: 'AUD' },
  { label: 'Currency Symbol *', key: 'currency_symbol', placeholder: 'A$' },
  { label: 'Currency Name', key: 'currency_name', placeholder: 'Australian Dollar' },
  { label: 'Sale Price *', key: 'base_price', type: 'number', placeholder: '99' },
  { label: 'Original Price (strikethrough)', key: 'original_price', type: 'number', placeholder: '199' },
  { label: 'Tax Rate %', key: 'tax_rate', type: 'number', placeholder: '10' },
  { label: 'Tax Label', key: 'tax_label', placeholder: 'GST' },
  { label: 'Badge Text', key: 'badge_text', placeholder: 'BEST VALUE' },
];

const REACCESS_FIELDS = [
  {
    label: 'Re-access Price', key: 'reaccess_price', type: 'number', placeholder: '999',
    hint: "Price to unlock a completed lesson, in this country's currency. Leave blank to fall back to the global Re-access Package (Settings → Re-access Packages).",
  },
  { label: 'Re-access Package Name', key: 'reaccess_package_name', placeholder: 'Study Again Pass' },
];

export const AdminCountryPricing = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyCode, setBusyCode] = useState(null);

  const fetchCountries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/admin/country-pricing`, { headers });
      setCountries(data);
    } catch (e) {
      toast.error('Failed to load countries');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchCountries(); }, [fetchCountries]);

  const openCreate = () => {
    setEditingCountry(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (country) => {
    setEditingCountry(country);
    setForm({ ...EMPTY_FORM, ...country });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.country_code || !form.country_name || !form.currency_code || !form.base_price) {
      toast.error('Country code, name, currency code, and price are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        base_price: Number(form.base_price),
        original_price: Number(form.original_price || form.base_price),
        tax_rate: Number(form.tax_rate || 0),
        reaccess_price: form.reaccess_price === '' ? null : Number(form.reaccess_price),
      };
      if (editingCountry) {
        await axios.put(`${API_URL}/admin/country-pricing/${editingCountry.country_code}`, payload, { headers });
        toast.success(`${form.country_name} updated`);
      } else {
        await axios.post(`${API_URL}/admin/country-pricing`, payload, { headers });
        toast.success(`${form.country_name} added`);
      }
      setShowModal(false);
      fetchCountries();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Save failed';
      toast.error(typeof msg === 'string' ? msg : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (country) => {
    setBusyCode(country.country_code);
    try {
      await axios.post(`${API_URL}/admin/country-pricing/${country.country_code}/toggle`, {}, { headers });
      fetchCountries();
    } catch {
      toast.error('Toggle failed');
    } finally {
      setBusyCode(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusyCode(deleteTarget.country_code);
    try {
      await axios.delete(`${API_URL}/admin/country-pricing/${deleteTarget.country_code}`, { headers });
      toast.success(`${deleteTarget.country_name} removed`);
      setDeleteTarget(null);
      fetchCountries();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || 'Delete failed';
      toast.error(msg);
    } finally {
      setBusyCode(null);
    }
  };

  const toggleGateway = (gw) => {
    setForm((f) => {
      const gws = f.payment_gateways || [];
      return {
        ...f,
        payment_gateways: gws.includes(gw) ? gws.filter((g) => g !== gw) : [...gws, gw],
      };
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Globe className="w-6 h-6" /> Country Pricing
          </h1>
          <p style={{ margin: '4px 0 0', color: 'var(--muted-foreground, #6b7280)', fontSize: 14, maxWidth: 560 }}>
            Configure prices, taxes, and payment methods per country. Visitors see their local
            currency automatically based on their location.
          </p>
        </div>
        <Button onClick={openCreate} className="rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Add Country
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {countries.map((country) => {
            const base = parseFloat(country.base_price);
            const tax = base * parseFloat(country.tax_rate) / 100;
            const total = (base + tax).toFixed(2);
            const flag = COUNTRY_FLAGS[country.country_code] || '🌍';

            return (
              <div
                key={country.country_code}
                className="bg-card"
                style={{
                  border: `2px solid ${country.enabled ? 'var(--border, #e5e7eb)' : '#fecaca'}`,
                  borderRadius: 12, padding: 20,
                  opacity: country.enabled ? 1 : 0.7,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 28 }}>{flag}</span>
                    <div>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 16 }}>{country.country_name}</p>
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--muted-foreground, #6b7280)' }}>
                        {country.country_code} · {country.currency_code}
                      </p>
                    </div>
                  </div>
                  <span style={{
                    background: country.enabled ? '#f0fdf4' : '#fef2f2',
                    color: country.enabled ? '#16a34a' : '#dc2626',
                    border: `1px solid ${country.enabled ? '#86efac' : '#fecaca'}`,
                    borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 700,
                  }}>
                    {country.enabled ? '● Active' : '○ Disabled'}
                  </span>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                    {Number(country.original_price) > base && (
                      <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: 14 }}>
                        {country.currency_symbol}{Number(country.original_price).toLocaleString()}
                      </span>
                    )}
                    <span style={{ fontSize: 24, fontWeight: 800, color: '#A67C52' }}>
                      {country.currency_symbol}{base.toLocaleString()}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--muted-foreground, #6b7280)' }}>
                    + {country.tax_rate}% {country.tax_label} ({country.currency_symbol}{total} inclusive)
                  </p>
                </div>

                {country.reaccess_price ? (
                  <div style={{
                    marginBottom: 16, padding: '6px 10px', background: 'var(--muted, #f9fafb)',
                    borderRadius: 6, fontSize: 12, color: 'var(--muted-foreground, #6b7280)',
                  }}>
                    🔁 Re-access: {country.currency_symbol}{country.reaccess_price}
                    {' '}({country.reaccess_package_name || 'Study Again Pass'})
                  </div>
                ) : (
                  <div style={{
                    marginBottom: 16, padding: '6px 10px', background: 'var(--muted, #f9fafb)',
                    borderRadius: 6, fontSize: 12, color: 'var(--muted-foreground, #6b7280)',
                  }}>
                    🔁 Re-access: using global default
                  </div>
                )}

                <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
                  {(country.payment_gateways || []).map((gw) => (
                    <span key={gw} style={{
                      background: GATEWAY_BADGES[gw]?.color || '#374151', color: 'white',
                      borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                    }}>
                      {GATEWAY_BADGES[gw]?.label || gw}
                    </span>
                  ))}
                  {(country.payment_gateways || []).length === 0 && (
                    <span style={{ fontSize: 12, color: '#dc2626' }}>No gateways enabled</span>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(country)}>
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                  </Button>
                  <Button
                    variant="outline" size="sm" className="flex-1"
                    disabled={busyCode === country.country_code}
                    onClick={() => handleToggle(country)}
                    style={{ color: country.enabled ? '#dc2626' : '#16a34a' }}
                  >
                    {country.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  {country.country_code !== 'IN' && (
                    <Button
                      variant="outline" size="sm"
                      disabled={busyCode === country.country_code}
                      onClick={() => setDeleteTarget(country)}
                      style={{ color: '#dc2626' }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCountry ? `Edit ${editingCountry.country_name}` : 'Add Country'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {FORM_FIELDS.map((field) => (
              <div key={field.key}>
                <Label className="text-xs font-semibold mb-1 block">{field.label}</Label>
                {field.hint && (
                  <p className="text-[11px] text-muted-foreground mb-1">{field.hint}</p>
                )}
                <Input
                  type={field.type || 'text'}
                  value={form[field.key] ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  disabled={field.lockOnEdit && !!editingCountry}
                />
              </div>
            ))}

            <div className="pt-2 border-t">
              <Label className="text-xs font-semibold mb-1 block">🔁 Re-access ("Study Again") Pricing</Label>
              <div className="space-y-3 mt-2">
                {REACCESS_FIELDS.map((field) => (
                  <div key={field.key}>
                    <Label className="text-xs font-semibold mb-1 block">{field.label}</Label>
                    {field.hint && (
                      <p className="text-[11px] text-muted-foreground mb-1">{field.hint}</p>
                    )}
                    <Input
                      type={field.type || 'text'}
                      value={form[field.key] ?? ''}
                      onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold mb-2 block">Payment Gateways</Label>
              <div className="flex flex-col gap-2">
                {ALL_GATEWAYS.map((gw) => (
                  <label key={gw} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={(form.payment_gateways || []).includes(gw)}
                      onCheckedChange={() => toggleGateway(gw)}
                    />
                    <span style={{
                      background: GATEWAY_BADGES[gw]?.color || '#374151', color: 'white',
                      borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 600,
                    }}>
                      {GATEWAY_BADGES[gw]?.label || gw}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 pt-1">
              <Switch
                checked={!!form.enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
              />
              <span className="text-sm font-semibold">Active (visible to users)</span>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#A67C52] hover:bg-[#8f6a45]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingCountry ? 'Save Changes' : 'Add Country'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete {deleteTarget?.country_name}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This removes {deleteTarget?.country_name}'s pricing config. Visitors from this country
            will fall back to India pricing. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              onClick={handleDelete}
              disabled={busyCode === deleteTarget?.country_code}
              style={{ background: '#dc2626', color: 'white' }}
            >
              {busyCode === deleteTarget?.country_code ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCountryPricing;
